import { Loader2, MapPin, Tag } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Hint } from "@/components/ui/hint";
import type { DashboardProps } from "./shared/types";
import { CompactStat, KpiCard, StatLabel } from "./shared/stat-card";
import { formatCurrency } from "./shared/formatters";
import { RevenueChart } from "./shared/revenue-chart";
import { DISCOUNT_SOURCE, DiscountTable } from "./shared/discount-table";

export function Dashboard(props: DashboardProps) {
  const { periodStats, periodDetails, periodLoading, detailsLoading, groupedReferrals, totalReferrals, includeAssessment } = props;

  if (periodLoading) {
    return (
      <div className="flex items-center gap-2 py-8">
        <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />
        <span className="text-sm text-admin-text-subtle">Laster...</span>
      </div>
    );
  }

  const s = periodStats;
  const revenue = parseFloat(s?.revenue ?? "0") / 100;
  const singleRevenue = parseFloat(s?.singlePaymentRevenue ?? "0") / 100;
  const totalRevenue = revenue + singleRevenue;
  const refunded = parseFloat(s?.totalRefunded ?? "0") / 100;
  const net = totalRevenue - refunded;

  const payments = s?.recurringPayments ?? 0;
  const newPayments = s?.newCustomerPayments ?? 0;
  const newRevenue = parseFloat(s?.newCustomerRevenue ?? "0") / 100;
  const paymentSplit = [
    { label: "Nye", count: newPayments, amount: newRevenue },
    { label: "Fornyede", count: payments - newPayments, amount: revenue - newRevenue },
    { label: "Totalt", count: payments, amount: revenue },
  ];

  // Lab cost is two separate negotiations and the page is misread when they
  // arrive as one number: the markers Furst bills us for, and the fee the
  // draw site charges for taking the blood. The two averages below are the
  // same cost with and without that fee, which is the whole difference
  // between them.
  const reqBioCost = parseFloat(s?.labCostInPeriod ?? "0");
  const directBioCost = parseFloat(s?.labDirectFromFurstCostInPeriod ?? "0");
  const bioCost = reqBioCost + directBioCost;
  const facilityCost = parseFloat(s?.labFacilityCostInPeriod ?? "0");
  const labCost = bioCost + facilityCost;

  // A doctor writing the assessment is our own work, so it is kept out of the
  // lab figures and their averages — but it is a real cost of every completed
  // visit, so the result has to carry it. Set in Katalog → Priser. The toggle
  // lifts it back out, because we never charged it before and every historical
  // figure moves when we do.
  const assessmentCost = parseFloat(s?.doctorsAssessmentCostInPeriod ?? "0");
  const assessmentRate = s?.doctorsAssessmentRate ?? null;
  const assessmentRateMissing = s != null && assessmentRate == null;
  const profit = net - labCost - (includeAssessment ? assessmentCost : 0);

  // The visits the costs above are measured over — same figure the Labbesok
  // card counts. Taken from periodStats so the averages are not blank while
  // the (slower) details request is still in flight.
  const labVisits = s?.labVisits ?? 0;
  const avgPerVisit = labVisits > 0 ? bioCost / labVisits : 0;
  const avgPerVisitWithFacility = labVisits > 0 ? labCost / labVisits : 0;

  const lb = periodDetails?.labBreakdown;
  const helfoShare = s?.labVisitsWithHelfoPercent;

  // The hovers spell out the arithmetic with the live figures and the rows
  // the backend counts, so a wrong number can be traced to its input.
  const kr = formatCurrency;
  const labCostSum = [
    `${kr(reqBioCost)} biomarkører på privat rekvisisjon`,
    `+ ${kr(directBioCost)} biomarkører uten rekvisisjon (Furst-direkte)`,
    `+ ${kr(facilityCost)} prøvetaking`,
    `= ${kr(labCost)}`,
  ].join("\n");
  const visitSource =
    "lab_visits med visit_date i perioden, self_reported = false og status allBiometricResultsReceived eller assessmentCompleted. Bedriftsbesøk (company_id satt) bare når de er slått på over.";

  return (
    <div className="space-y-6">
      {/* Row 1: what happened in the period — volume, and the money on it */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="col-span-2">
          <CardContent className="px-4 py-3">
            <StatLabel
              label="Betalinger"
              explain={[
                `Totalt: ${payments} fakturaer = ${kr(revenue)}`,
                `Nye: ${newPayments} = ${kr(newRevenue)}`,
                `Fornyede: ${payments} − ${newPayments} = ${payments - newPayments} stk, ${kr(revenue)} − ${kr(newRevenue)} = ${kr(revenue - newRevenue)}`,
                "",
                "Totalt: invoices med charge_date i perioden. Beløp = SUM(amount) ÷ 100.",
                "Ny: kundens første faktura (tidligste charge_date), med mindre kunden har et abonnement (active/stopped) som startet over en måned før — betalinger fra før august 2025 finnes ikke som fakturaer.",
                "Interne kontoer (@stadler.no, @amino.no) og betalinger som ikke peker på en gyldig auth-bruker er utelatt.",
              ].join("\n")}
            />
            <div className="grid grid-cols-3 gap-3">
              {paymentSplit.map((p) => (
                <div key={p.label}>
                  <p className="text-xs text-admin-text-subtle">{p.label}</p>
                  <p className="text-lg font-bold text-admin-text-primary">{p.count} stk</p>
                  <p className="text-sm text-admin-text-secondary">{formatCurrency(p.amount)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <KpiCard
          label="Enkeltkjøp"
          count={`${s?.singlePayments ?? 0} stk`}
          amount={formatCurrency(singleRevenue)}
          explain={[
            `${s?.singlePayments ?? 0} stk = ${kr(singleRevenue)}`,
            "",
            "single_payments med status paid og updated_at i perioden — sist endret, ikke betalingstidspunktet. Beløp = SUM(amount) ÷ 100. Kommer i tillegg til Betalinger.",
            "Inkluderer abonnementsoppgraderinger (kind upgrade_*). Interne kontoer (@stadler.no, @amino.no) og betalinger som ikke peker på en gyldig auth-bruker er utelatt.",
          ].join("\n")}
        />
        <KpiCard
          label="Refusjoner"
          count={`${s?.refundCount ?? 0} stk`}
          amount={formatCurrency(refunded)}
          color="text-admin-danger-400"
          explain={[
            `${s?.refundCount ?? 0} stk = ${kr(refunded)}`,
            "",
            "invoice_refunds med refund_date i perioden, uansett når fakturaen ble trukket. Beløp = SUM(amount) ÷ 100. Trekkes fra i Netto.",
            "Interne kontoer (@stadler.no, @amino.no) og refusjoner på betalinger uten gyldig auth-bruker er utelatt.",
          ].join("\n")}
        />
        <KpiCard
          label="Labbesøk"
          count={`${labVisits} stk`}
          amount={formatCurrency(labCost)}
          subtitle={helfoShare ? `${helfoShare}% helfo` : undefined}
          explain={[
            `${labVisits} besøk, ${s?.labVisitsWithHelfo ?? 0} med Helfo-rekvisisjon (${helfoShare ?? 0}%)`,
            "",
            labCostSum,
            "",
            `${visitSource} Legevurdering er ikke med.`,
          ].join("\n")}
        />
      </div>

      {/* Row 2: the result, as the subtraction it is. The labels carry the
          arithmetic so nobody has to guess which card feeds which. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CompactStat
          label="Netto"
          value={formatCurrency(net)}
          hint="betalinger + enkeltkjøp − refusjoner"
          explain={`${kr(revenue)} betalinger + ${kr(singleRevenue)} enkeltkjøp − ${kr(refunded)} refusjoner = ${kr(net)}\n\nBeløpene fra kortene over, ingen egen spørring.`}
        />
        <CompactStat
          label="− Labkost"
          value={formatCurrency(labCost)}
          hint={`${formatCurrency(bioCost)} biomarkører + ${formatCurrency(facilityCost)} prøvetaking`}
          explain={[
            labCostSum,
            "",
            "Biomarkører: biometric_prices for prisen valgt over, én gang per Furst-kode per besøk (høyeste pris). En kode på en Helfo-rekvisisjon teller 0, og en kode uten pris teller 0. Rekvisisjoner teller med status submitted, awaitingResults eller allResultsReceived.",
            "Prøvetaking: labens lab_prices-rad når den har nøyaktig én, ellers 0. Alltid 0 på en Furst-tilknyttet lab når besøket har en Helfo-rekvisisjon.",
            "Samme besøk som Labbesøk.",
          ].join("\n")}
        />
        <CompactStat
          label={includeAssessment ? "− Legevurdering" : "Legevurdering (ikke trukket fra)"}
          value={formatCurrency(assessmentCost)}
          hint={
            assessmentRate
              ? `${formatCurrency(assessmentRate)} × ${labVisits} besøk`
              : "ingen sats lagt inn"
          }
          color={
            !includeAssessment
              ? "text-admin-text-subtle"
              : assessmentRateMissing
                ? "text-admin-warn-400"
                : undefined
          }
          explain={[
            `${assessmentRate ? kr(assessmentRate) : "ingen sats (0 kr)"} × ${labVisits} labbesøk = ${kr(assessmentCost)}`,
            "",
            "Satsen er legevurderingsraden i prices (Katalog → Priser), besøkene de samme som Labbesøk. Vår egen legetid, så den er ikke med i snittene under.",
            includeAssessment ? "Trukket fra i Resultat." : "Ikke trukket fra i Resultat: «Uten» er valgt.",
          ].join("\n")}
        />
        <CompactStat
          label="= Resultat"
          value={formatCurrency(profit)}
          color={profit >= 0 ? "text-admin-success-500" : "text-admin-danger-500"}
          hint={includeAssessment ? "netto − labkost − legevurdering" : "netto − labkost"}
          explain={
            includeAssessment
              ? `${kr(net)} netto − ${kr(labCost)} labkost − ${kr(assessmentCost)} legevurdering = ${kr(profit)}`
              : `${kr(net)} netto − ${kr(labCost)} labkost = ${kr(profit)}\n\nLegevurdering er ikke trukket fra: «Uten» er valgt.`
          }
        />
      </div>
      {includeAssessment && assessmentRateMissing && (
        <p className="text-xs text-admin-warn-400">
          Ingen sats for legevurdering er lagt inn, så den teller som 0 kr og
          resultatet er for høyt. Sett den i Katalog → Priser.
        </p>
      )}

      {/* Row 3: the same lab cost per visit, with and without the draw fee —
          the pair only makes sense read together. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CompactStat
          label="Snitt/besøk — biomarkører"
          value={formatCurrency(avgPerVisit)}
          hint={labVisits > 0 ? `${formatCurrency(bioCost)} ÷ ${labVisits} besøk` : undefined}
          explain={[
            `(${kr(reqBioCost)} privat rekvisisjon + ${kr(directBioCost)} Furst-direkte) ÷ ${labVisits} besøk = ${kr(avgPerVisit)}`,
            "",
            "Uten prøvetaking og legevurdering. Helfo-besøk er med i nevneren, men koster oss 0, så snittet trekkes ned av dem.",
          ].join("\n")}
        />
        <CompactStat
          label="Snitt/besøk — m/prøvetaking"
          value={formatCurrency(avgPerVisitWithFacility)}
          hint={labVisits > 0 ? `${formatCurrency(labCost)} ÷ ${labVisits} besøk` : undefined}
          explain={[
            `(${kr(bioCost)} biomarkører + ${kr(facilityCost)} prøvetaking) ÷ ${labVisits} besøk = ${kr(avgPerVisitWithFacility)}`,
            "",
            "Hele labkosten delt på alle labbesøk, også de uten gebyr. Legevurdering er ikke med.",
          ].join("\n")}
        />
      </div>

      {/* Row 4: Monthly chart */}
      {periodDetails && <RevenueChart monthlySeries={periodDetails.monthlySeries} />}

      {/* Row 5: Detail cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <KundekildeSection groupedReferrals={groupedReferrals} totalReferrals={totalReferrals} />
          {periodDetails ? (
            <DiscountTable discountDetails={periodDetails.discountDetails} />
          ) : s?.discountCodes && s.discountCodes.length > 0 && (
            <DiscountFallback discountCodes={s.discountCodes} />
          )}
        </div>

        <div className="space-y-4">
          {detailsLoading ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />
              <span className="text-sm text-admin-text-subtle">Laster detaljer...</span>
            </div>
          ) : (
            <>
              {lb && <LabBreakdownCard labBreakdown={lb} />}
              {periodDetails && periodDetails.paymentBreakdown.byTier.length > 0 && (
                <TierCard byTier={periodDetails.paymentBreakdown.byTier} single={periodDetails.paymentBreakdown.single} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Kundekilde ───────────────────────────────────────────────

function KundekildeSection({ groupedReferrals, totalReferrals }: { groupedReferrals: DashboardProps["groupedReferrals"]; totalReferrals: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-admin-primary" />
            <p className="text-sm font-medium text-admin-text-primary">
              <Hint text={`${totalReferrals} registreringer = summen av tallene under\n\nmarketing_referrals med createdAt i perioden, filtrert i nettleseren. Gruppert på teksten før «: » i navnet; resten er underkategorien. Ikke koblet til betalinger.`}>
                Kundekilde
              </Hint>
            </p>
          </div>
          <span className="text-xs text-admin-text-subtle">{totalReferrals} i perioden</span>
        </div>
        {totalReferrals === 0 ? (
          <p className="text-xs text-admin-text-subtle">Ingen registrerte kilder i perioden</p>
        ) : (
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(groupedReferrals)
            .sort(([, a], [, b]) => b.count - a.count)
            .map(([baseName, group]) => {
              const displayName = baseName === "othervdos" ? "Annet" : baseName.charAt(0).toUpperCase() + baseName.slice(1);
              return (
                <div key={baseName} className="rounded-lg border border-admin-card-border p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="h-3.5 w-3.5 text-admin-primary shrink-0" />
                    <span className="text-sm font-medium text-admin-text-primary">{displayName}</span>
                    <Badge variant="secondary" className="ml-auto text-[10px]">{group.count}</Badge>
                  </div>
                  {group.items.length > 0 && (
                    <div className="mt-1.5 space-y-0.5 pl-5">
                      {group.items
                        .sort((a, b) => b.count - a.count)
                        .map((item) => (
                          <div key={item.label} className="flex items-center justify-between text-xs text-admin-text-secondary">
                            <span className="truncate mr-2">{item.label}</span>
                            <span className="shrink-0">{item.count}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Lab Breakdown Card ───────────────────────────────────────

function LabBreakdownCard({ labBreakdown }: { labBreakdown: NonNullable<DashboardProps["periodDetails"]>["labBreakdown"] }) {
  const facilityCost = parseFloat(labBreakdown.standardFacilityCost ?? "0");
  const directCost = parseFloat(labBreakdown.directFromFurstCost ?? "0");
  const bioCost = parseFloat(labBreakdown.standardCost ?? "0") + directCost;
  const feeVisitBioCost = parseFloat(labBreakdown.feeVisitBioCost ?? "0");
  const totalCount = labBreakdown.totalCount ?? 0;
  const facilityVisitCount = labBreakdown.facilityVisitCount ?? 0;
  const avgWithFacility = facilityVisitCount > 0 ? (feeVisitBioCost + facilityCost) / facilityVisitCount : 0;
  const standardCount = labBreakdown.standardCount ?? 0;
  const helfoCount = labBreakdown.helfoCount ?? 0;
  const kr = formatCurrency;

  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm font-medium text-admin-text-primary mb-3">Labfordeling</p>
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <Hint
              className="text-admin-text-subtle"
              text={`${standardCount} besøk med en privat (ikke-Helfo) rekvisisjon, eller uten rekvisisjon i det hele tatt. Et besøk med både privat og Helfo-rekvisisjon telles både her og under Helfo.\n\nBeløp: biomarkørene på de private rekvisisjonene = ${kr(labBreakdown.standardCost)}`}
            >
              Standard (rekvirert)
            </Hint>
            <span className="text-admin-text-secondary">{labBreakdown.standardCount} stk — {formatCurrency(labBreakdown.standardCost)}</span>
          </div>
          {directCost > 0 && (
            <div className="flex justify-between text-xs">
              <Hint
                className="text-admin-text-subtle"
                text={`biometric_results på standard-besøk der Furst-koden ikke står på noen rekvisisjon på besøket = ${kr(directCost)}`}
              >
                Ukjent kilde (Furst-direkte)
              </Hint>
              <span className="text-admin-text-secondary">{formatCurrency(labBreakdown.directFromFurstCost)}</span>
            </div>
          )}
          <div className="flex justify-between text-xs">
            <Hint
              className="text-admin-text-subtle"
              text={`${helfoCount} besøk med minst én Helfo-rekvisisjon. Markørene der koster oss 0.`}
            >
              Helfo
            </Hint>
            <span className="text-admin-text-secondary">{labBreakdown.helfoCount} stk — gratis</span>
          </div>
          <div className="flex justify-between text-xs border-t border-admin-card-border pt-2">
            <Hint
              className="text-admin-text-subtle font-medium"
              text={`${totalCount} unike besøk — ikke ${standardCount} + ${helfoCount}, fordi delte besøk står i begge.\n${kr(labBreakdown.standardCost)} privat rekvisisjon + ${kr(directCost)} Furst-direkte = ${kr(bioCost)}\n\nSamme besøk som Labbesøk. Uten prøvetaking.`}
            >
              Totalt
            </Hint>
            <span className="text-admin-text-primary font-medium">
              {totalCount} stk — {formatCurrency(bioCost)}
            </span>
          </div>

          <div className="mt-3 pt-3 border-t border-admin-card-border space-y-2">
            <p className="text-xs font-medium text-admin-text-primary">Prøvetakingsgebyr</p>
            <div className="flex justify-between text-xs">
              <Hint
                className="text-admin-text-subtle"
                text={`${facilityVisitCount} besøk der gebyret er over 0, til sammen ${kr(facilityCost)}\n\nGebyret er labens lab_prices-rad når den har nøyaktig én, ellers 0. Alltid 0 på en Furst-tilknyttet lab når besøket har en Helfo-rekvisisjon.`}
              >
                Besøk med gebyr
              </Hint>
              <span className="text-admin-text-secondary">{labBreakdown.facilityVisitCount} stk — {formatCurrency(facilityCost)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <Hint
                className="text-admin-text-subtle font-medium"
                text={`(${kr(feeVisitBioCost)} biomarkører + ${kr(facilityCost)} gebyr) ÷ ${facilityVisitCount} besøk = ${kr(avgWithFacility)}\n\nBåde biomarkørene og gebyret er summert over de samme besøkene: de med gebyr over 0.`}
              >
                Snitt totalkost/besøk m/gebyr
              </Hint>
              <span className="text-admin-text-primary font-medium">{formatCurrency(avgWithFacility)}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Tier Card ────────────────────────────────────────────────

function TierCard({ byTier, single }: {
  byTier: { tier: string; count: number; amount: string }[];
  single: { count: number; amount: string };
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm font-medium text-admin-text-primary mb-3">
          <Hint text="Tiers: invoices med charge_date i perioden, gruppert på tier. Beløp = SUM(amount) ÷ 100. Samme fakturaer som Betalinger, uten interne kontoer (@stadler.no, @amino.no) og betalinger uten gyldig auth-bruker.\n\nEnkelt: samme enkeltkjøp som Enkeltkjøp-kortet (single_payments med status paid og updated_at i perioden).">
            Betalingsfordeling
          </Hint>
        </p>
        <div className="space-y-1.5">
          {byTier.map((t) => (
            <div key={t.tier} className="flex items-center justify-between text-xs">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{t.tier}</Badge>
              <span className="text-admin-text-secondary">{t.count} stk — {formatCurrency(t.amount, true)}</span>
            </div>
          ))}
          {single.count > 0 && (
            <div className="flex items-center justify-between text-xs border-t border-admin-card-border pt-1.5">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">enkelt</Badge>
              <span className="text-admin-text-secondary">{single.count} stk — {formatCurrency(single.amount, true)}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Discount Fallback ────────────────────────────────────────

function DiscountFallback({ discountCodes }: { discountCodes: { discount: unknown; count: number }[] }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-4 pt-4 pb-2 flex items-center gap-2">
          <Tag className="h-4 w-4 text-admin-primary" />
          <Hint className="text-sm font-medium text-admin-text-primary" text={DISCOUNT_SOURCE}>Rabattkoder</Hint>
          <span className="text-xs text-admin-text-subtle">({discountCodes.length} koder)</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-admin-card-border">
              <th className="text-left text-xs font-medium text-admin-text-subtle px-4 py-2">Kode</th>
              <th className="text-right text-xs font-medium text-admin-text-subtle px-4 py-2">Antall</th>
            </tr>
          </thead>
          <tbody>
            {discountCodes.map((d, i) => {
              const code = typeof d.discount === "object" && d.discount !== null
                ? (d.discount as Record<string, unknown>).code ?? JSON.stringify(d.discount)
                : String(d.discount);
              return (
                <tr key={i} className="border-b border-admin-card-border last:border-0">
                  <td className="px-4 py-2 font-mono text-admin-text-primary">{String(code)}</td>
                  <td className="px-4 py-2 text-right text-admin-text-secondary">{d.count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
